import React, { FormEventHandler, useRef, ReactNode } from 'react'
import { StatelessInputGroup } from './StatelessInputGroup'
import { StatelessFormProps } from './StatelessForm.types'
import { ButtonCancel, ButtonSubmit } from 'common/ui/Buttons'
 

interface Props extends StatelessFormProps {
  children?:  ReactNode
}
export const StatelessForm = ({
  className,
  viewMode = false,
  ...props
}: Props): JSX.Element => {

  className = `stateless-form ${className ||``}`.trim()

  const { buttonText, formFields, formTabs, handleSelect, handleSubmit, handleBlur, handleCancel, inputGroupHandlers, children, FormContainer} = props
 
  const formRef = useRef<HTMLFormElement>(null)

  const getFD: FormEventHandler<HTMLFormElement> = (e) => {
    const formEl = formRef?.current
    if (!formEl) return

    const messageEl = formEl.querySelector('[data-id="form-message"]') as HTMLElement

    if (formEl.querySelectorAll('.is-required')?.length) {
      formEl.classList.add('has-errors')  
      if (messageEl) messageEl.textContent = 'Please fill out all required fields'
      return
    }

    if (formEl.querySelectorAll('.has-errors')?.length) {
      formEl.classList.add('has-errors')  
      if (messageEl) messageEl.textContent = 'Please check the form for errors'
      return
    }
    
    const fd = Object.fromEntries(new FormData(e.currentTarget))
    return fd;
  }

  const submitHandler: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    const fd = getFD(e)
    return handleSubmit?.(fd);
  }

  const blurHandler: FormEventHandler<HTMLFormElement> = (e) => {
    if (!e?.target?.value) return
    const fd = getFD(e)
    return handleBlur?.(fd);
  }

  const FormComponent = (x?: Record<string, unknown>) => {
    const { children, ...props } = x || {}
    if (FormContainer) return <FormContainer className={`stateless-form${viewMode ? ' view-mode' : ''} ${className}`} ref={formRef} onSubmit={submitHandler} {...props}>{children as JSX.Element}</FormContainer>
    return <form className={`v1 stateless-form${viewMode ? ' view-mode' : ''} ${className}`} ref={formRef} onSubmit={submitHandler}  onBlur={blurHandler} {...props}>{children as JSX.Element}</form>
  }

  const toggleView = (tabName: string) => {
    formRef.current?.querySelectorAll('[data-tab]')?.forEach(el => {
      if (el.getAttribute('data-tab') === tabName) {
        el.classList.add('active')
        el.classList.remove('hidden')
        el.style.visibility = 'visible'
      }
      else {
        el.classList.remove('active')
        el.classList.add('hidden')
        el.style.visibility = 'hidden'
      }
    })
  }

  return (
    <FormComponent>
      <div data-id='form-message' className='stateless-form-message'></div>
      
      {formFields &&
      <div className='stateless-form-fields'>
        {formFields?.map((field, i) => (
          !field?.spacer 
          ? <StatelessInputGroup key={field.name} viewMode={viewMode} {...{handleSelect,...field}} {...inputGroupHandlers} />
          : <div key={`spacer-${i}`} className='stateless-form-spacer'>{field?.spacer || ' '}</div>
      ))}
      </div>}
      {formTabs && <div className='stateless-form-tabs'>
        <div className='stateless-form-tabs-nav'>
          {Object.keys(formTabs).map((tabName, i) => 
            <button type='button' key={tabName} className={`stateless-form-tab-name${i === 0 ? ' active' : ''}`} onClick={() => toggleView(tabName)}>{tabName}</button>
          )}
        </div>
        {Object.entries(formTabs).map(([tabName, tabFields], i) => {
            return <div key={tabName} className={`stateless-form-tab${i === 0 ? ' active' : ' hidden'}`} data-tab={tabName}>
              <div className='stateless-form-tab-fields'>
                {tabFields?.map((field, i) => (
                  !field?.spacer 
                    ? <StatelessInputGroup  viewMode={viewMode} key={field.name} {...{handleSelect,...field}}  {...inputGroupHandlers} />
                    : <div key={`spacer-${i}`} className='stateless-form-spacer'>{field?.spacer || ' '}</div>
                ))}
              </div>
            </div>
          })}
      </div>
      }
      {children && 
        <div className='stateless-form-children'>
          {children}
        </div>}
      <div className='stateless-form-buttons'>
        {!viewMode ? <>
        {!className.includes('hide-cancel') && <ButtonCancel className='cancel' type='button' onClick={() => handleCancel?.()}>{buttonText?.cancel || `Cancel`}</ButtonCancel> }
        {!className.includes('hide-submit') && <ButtonSubmit className='submit' value={buttonText?.submit}/>}
        </> : <></>}
      
      </div>
    </FormComponent>
  )
}